import { useEffect, useState, useCallback } from "react";
import { DownloadSimple, ChartBar } from "@phosphor-icons/react";
import client from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

const TYPES = [
  ["revenue", "Revenue collected"],
  ["outstanding", "Outstanding invoices"],
  ["payouts", "Payouts paid"],
  ["commissions", "Commissions"],
  ["grades", "Photographer grades"],
];

const MONEY_COLS = new Set(["Amount", "Collected", "Paid out", "Outstanding", "Total", "Paid", "Unpaid"]);
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}
function monthStartISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function ReportsPanel() {
  const [type, setType] = useState("revenue");
  const [start, setStart] = useState(monthStartISO());
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    client.get("/admin/reports", { params: { type, start, end } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load report"))
      .finally(() => setLoading(false));
  }, [type, start, end]);

  useEffect(() => { load(); }, [load]);

  const preset = (days, label) => {
    if (label === "mtd") { setStart(monthStartISO()); setEnd(todayISO()); return; }
    setStart(todayISO(-days)); setEnd(todayISO());
  };

  const exportCsv = async () => {
    try {
      const r = await client.get("/admin/reports.csv", { params: { type, start, end }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `reelty-${type}-${start}-to-${end}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const isMoneyCol = (col) => MONEY_COLS.has(col);

  return (
    <div className="space-y-6" data-testid="reports-panel">
      {/* Controls */}
      <div className="lux-panel p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1.5" data-testid="report-type-tabs">
          {TYPES.map(([v, lbl]) => (
            <button key={v} data-testid={`report-type-${v}`} onClick={() => setType(v)}
              className={`px-3 py-1.5 rounded-[5px] text-sm font-medium border transition-colors ${type === v ? "bg-[#4A0000] text-white border-[#4A0000]" : "bg-white text-[#5C5A58] border-[#E9DDC6] hover:border-[#4A0000]/40"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-[#9A968E] mb-1">From</label>
            <input type="date" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)}
              className="border border-[#E9DDC6] rounded-md px-2.5 py-1.5 text-sm bg-white" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-[#9A968E] mb-1">To</label>
            <input type="date" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)}
              className="border border-[#E9DDC6] rounded-md px-2.5 py-1.5 text-sm bg-white" />
          </div>
          <Button data-testid="report-export" onClick={exportCsv} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
            <DownloadSimple size={16} className="mr-1.5" weight="bold" /> CSV
          </Button>
        </div>
        <div className="flex gap-1.5 basis-full" data-testid="report-presets">
          {[["mtd", "This month"], [30, "Last 30d"], [90, "Last 90d"], [365, "Last year"]].map(([v, lbl]) => (
            <button key={lbl} data-testid={`report-preset-${lbl}`} onClick={() => preset(v, v === "mtd" ? "mtd" : "range")}
              className="text-xs px-2.5 py-1 rounded-full border border-[#E9DDC6] text-[#6B6660] hover:border-[#4A0000]/40 hover:text-[#4A0000] transition-colors">{lbl}</button>
          ))}
        </div>
      </div>

      {/* Summary */}
      {data?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-summary">
          {Object.entries(data.summary).map(([label, value]) => (
            <div key={label} className="surface-kpi p-4 min-w-0">
              <p className="eyebrow text-[#9A968E] relative z-10">{label}</p>
              <p title={isMoneyCol(label) ? usd(value) : String(value)}
                className="font-display text-xl sm:text-2xl text-[#4A0000] mt-1 tabular-nums relative z-10 truncate">
                {isMoneyCol(label) ? usd(value) : value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="lux-panel overflow-hidden">
        {loading ? (
          <p className="py-16 text-center text-sm text-[#9A968E]">Loading…</p>
        ) : !data || data.rows.length === 0 ? (
          <p className="py-16 text-center text-sm text-[#9A968E] flex flex-col items-center gap-2" data-testid="report-empty">
            <ChartBar size={28} weight="duotone" className="text-[#B08D57]" />
            No data in this range.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FAF9F6] text-left text-[11px] uppercase tracking-wide text-[#8A8681]">
                  {data.columns.map((c) => (
                    <th key={c} className={`px-4 py-3 font-medium ${isMoneyCol(c) ? "text-right" : ""}`}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="report-rows">
                {data.rows.map((row, i) => (
                  <tr key={i} data-testid={`report-row-${i}`} className={`border-b border-[#FAF9F6] ${i % 2 ? "bg-[#FBFAF7]" : "bg-white"}`}>
                    {row.map((cell, j) => {
                      const col = data.columns[j];
                      const moneyCell = isMoneyCol(col);
                      return (
                        <td key={j} className={`px-4 py-2.5 ${moneyCell ? "text-right font-semibold text-[#4A0000] tabular-nums" : "text-[#2C2626]"}`}>
                          {moneyCell ? usd(cell) : String(cell)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
